import { Modal } from 'antd'
import { useMemo, useState } from 'react'
import { useParams } from 'react-router'
import { Icon } from '@iconify/react'

import { Button } from 'common/components/Button/Button'
import { Input } from 'common/components/Input/Input'
import { Label } from 'common/components/Label/Label'
import { Select } from 'common/components/Select/Select'
import { Show } from 'common/components/Show/Show'
import { BUTTON_MODIFIER, BUTTON_PROPORTION } from 'common/constants/buttonConstants'
import {
  useCreateReferralSourceMutation,
  useFetchEncounterPatientsQuery,
} from 'features/Procedure/state/procedure.api'

import styles from './patient-info-dialog.module.scss'
import { referralSourceExists, toReferralSourceOptions } from './patient-contact-form.schema'

type Props = {
  value?: string
  onChange: (value: string) => void
  error?: string
}

export const ReferralSourceField = ({ value, onChange, error }: Props) => {
  const { siteId } = useParams<{ siteId: string }>()
  const { data: encounterPatients } = useFetchEncounterPatientsQuery(
    { siteId: siteId! },
    { skip: !siteId },
  )
  const [createReferralSource, { isLoading: isCreating }] = useCreateReferralSourceMutation()

  const [isAddOpen, setIsAddOpen] = useState(false)
  const [newTitle, setNewTitle] = useState('')
  const [addError, setAddError] = useState<string | undefined>(undefined)

  const referralSources = encounterPatients?.referral_sources
  const options = useMemo(() => toReferralSourceOptions(referralSources), [referralSources])

  const openAddDialog = () => {
    setNewTitle('')
    setAddError(undefined)
    setIsAddOpen(true)
  }

  const closeAddDialog = () => {
    setIsAddOpen(false)
  }

  const handleNewTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewTitle(event.target.value)
    setAddError(undefined)
  }

  const handleAdd = async () => {
    const title = newTitle.trim()

    if (!title) {
      setAddError('Please enter a referral source')
      return
    }

    if (referralSourceExists(referralSources, title)) {
      setAddError('This referral source already exists')
      return
    }

    if (!siteId) return

    try {
      const response = await createReferralSource({ siteId, title }).unwrap()
      if (response?.status === false || response?.success === false) {
        setAddError(response.message || 'This referral source already exists')
        return
      }
      onChange(title)
      closeAddDialog()
    } catch {
      setAddError('Failed to add referral source')
    }
  }

  return (
    <div className={styles.referralField}>
      <Label label='Referral Source' />
      <div className={styles.referralRow}>
        <Select
          containerClassName={styles.referralSelect}
          className={styles.referralSelectInput}
          value={value || undefined}
          onChange={(selected) => onChange(selected ?? '')}
          listOptions={options}
          showSearch
          placeholder='Select referral source'
        />
        <button
          type='button'
          className={styles.referralAddButton}
          onClick={openAddDialog}
          aria-label='Add referral source'>
          <Icon icon='mdi:plus' width={20} height={20} />
        </button>
      </div>
      <Show when={!!error}>
        <p className='errorMessage'>{error}</p>
      </Show>

      <Modal
        open={isAddOpen}
        onCancel={closeAddDialog}
        footer={null}
        title='Add Referral Source'
        width={1260}
        maskClosable={false}
        destroyOnHidden>
        <div className={styles.addReferralDialog}>
          <Input
            label='Referral Source'
            name='referral_source_title'
            value={newTitle}
            onChange={handleNewTitleChange}
            error={addError}
          />
          <div className={styles.addReferralFooter}>
            <Button
              htmlType='button'
              modifier={BUTTON_MODIFIER.SECONDARY}
              proportion={BUTTON_PROPORTION.SMALL}
              disabled={isCreating}
              onClick={closeAddDialog}>
              Cancel
            </Button>
            <Button
              htmlType='button'
              modifier={BUTTON_MODIFIER.PRIMARY}
              proportion={BUTTON_PROPORTION.SMALL}
              disabled={isCreating}
              onClick={handleAdd}>
              Add
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
